import {
  AttributeGroupDraft,
  ProductModifierGroupDraft,
  ProductVariantEditor,
} from '@features/catalog/models/product-form-draft.models';
import {
  ProductGalleryImage,
  Product,
  ProductSupplySourceDraft,
  VariantInventoryState,
} from '@features/catalog/models/product.models';
import { mediaUrlForContext } from '@core/media/utils/media-image.util';
import { ProductFormModifierOrchestrationService } from '@features/catalog/services/product-form-modifier-orchestration.service';
import { buildAttributeGroupsFromProduct } from '@features/catalog/utils/product-attribute-groups-from-product.utils';
import { assignedCategoryIdsFromProduct } from '@features/catalog/utils/product-category-assignment.utils';
import { inferProductStructureFromProduct } from '@features/catalog/utils/product-form-publication.utils';
import { mapActiveProductVariantsToEditors } from '@features/catalog/utils/map-product-variant-to-editor.util';
import {
  resolveEffectiveInventoryManaged,
  resolveVariantInventoryState,
} from '@features/catalog/utils/product-inventory-state.utils';
import { ProductBundleFormRawValue } from '@features/catalog/utils/product-bundle-input.builder';
import { Injectable } from '@angular/core';

export interface ProductFormHydrationState {
  formValue: ProductBundleFormRawValue;
  imagePreviewUrl: string | null;
  maxGalleryImages: number;
  galleryImages: ProductGalleryImage[];
  productVariants: ProductVariantEditor[];
  modifierGroups: ProductModifierGroupDraft[];
  attributeGroups: AttributeGroupDraft[];
  /** Solo productos variables: agregado desde variantes (null si no aplica). */
  variantInventoryState: VariantInventoryState | null;
  /** Boolean agregado para hidratar el toggle "Controlar inventario". */
  inventoryManaged: boolean;
  /** Proveedores ya configurados como insumo (uno por proveedor, con precio general + ajustes por variante). */
  supplySources: ProductSupplySourceDraft[];
}

@Injectable()
export class ProductFormHydrationService {
  constructor(private readonly modifierOrchestration: ProductFormModifierOrchestrationService) {}

  hydrateFromProduct(product: Product, createTempId: () => string): ProductFormHydrationState {
    // El backend puede devolver `simple` aunque ya existan variantes; usamos
    // la misma inferencia que decide `productStructure` en el formulario
    // para no perder el agregado de inventario de un producto variable.
    const activeVariants = (product.productVariants ?? []).filter(
      (variant) => variant.active !== false
    );
    const structure = inferProductStructureFromProduct(
      product.productStructure,
      activeVariants.length
    );
    const variantInventoryState = resolveVariantInventoryState(
      structure,
      product.variantInventoryState,
      activeVariants
    );
    const inventoryManaged = resolveEffectiveInventoryManaged(
      structure,
      product.inventoryManaged,
      product.variantInventoryState,
      activeVariants,
      {
        inventoryItemId: product.inventoryItemId,
        stockQuantity: product.stockQuantity,
      }
    );

    return {
      formValue: this.buildFormValue(product, inventoryManaged),
      imagePreviewUrl: this.resolveImagePreviewUrl(product),
      maxGalleryImages: product.maxGalleryImages ?? 10,
      galleryImages: [...(product.galleryImages ?? [])],
      productVariants: mapActiveProductVariantsToEditors(product.productVariants),
      modifierGroups: this.modifierOrchestration.mapGroupsFromProduct(
        product.modifierGroups,
        createTempId
      ),
      attributeGroups: buildAttributeGroupsFromProduct(product, createTempId),
      variantInventoryState,
      inventoryManaged,
      supplySources: this.buildSupplySourcesDraft(product),
    };
  }

  /**
   * Un proveedor por fila en la UI (selección global de proveedores, precio
   * ajustable por variación): `product.supplySources` trae una fuente por
   * variante x proveedor. Se agrupa por proveedor, se toma el precio más
   * frecuente como "general" y solo las variantes cuyo precio difiere de ese
   * general quedan en `variantPrices` (para precargar el ajuste fino en el diálogo).
   */
  private buildSupplySourcesDraft(product: Product): ProductSupplySourceDraft[] {
    const byProvider = new Map<
      string,
      { providerName: string; entries: { variantId: string | null; price: number }[] }
    >();

    for (const source of product.supplySources ?? []) {
      const bucket = byProvider.get(source.providerId) ?? {
        providerName: source.providerName,
        entries: [],
      };
      bucket.entries.push({ variantId: source.productVariantId, price: source.purchasePrice });
      byProvider.set(source.providerId, bucket);
    }

    return Array.from(byProvider.entries()).map(([providerId, { providerName, entries }]) => {
      const generalPrice = this.mostCommonPrice(entries.map((entry) => entry.price));
      const variantPrices: Record<string, number> = {};
      for (const entry of entries) {
        if (entry.variantId && entry.price !== generalPrice) {
          variantPrices[entry.variantId] = entry.price;
        }
      }

      return {
        providerId,
        providerName,
        purchasePrice: generalPrice,
        ...(Object.keys(variantPrices).length ? { variantPrices } : {}),
      };
    });
  }

  private mostCommonPrice(prices: readonly number[]): number {
    const counts = new Map<number, number>();
    for (const price of prices) {
      counts.set(price, (counts.get(price) ?? 0) + 1);
    }

    let best = prices[0] ?? 0;
    let bestCount = 0;
    for (const [price, count] of counts) {
      if (count > bestCount) {
        best = price;
        bestCount = count;
      }
    }
    return best;
  }

  buildAttributeGroupsFromProduct(
    product: Product,
    createTempId: () => string
  ): AttributeGroupDraft[] {
    return buildAttributeGroupsFromProduct(product, createTempId);
  }

  private buildFormValue(
    product: Product,
    inventoryManaged: boolean
  ): ProductBundleFormRawValue {
    const variantCount = (product.productVariants ?? []).filter(
      (variant) => variant.active !== false
    ).length;
    return {
      name: product.name,
      price: product.price,
      productStructure: inferProductStructureFromProduct(product.productStructure, variantCount),
      description: product.description ?? '',
      imageUrl: product.imageUrl ?? '',
      publicVisible: product.publicVisible,
      posVisible: product.posVisible,
      catalogBestSellerEnabled: product.catalogBestSellerEnabled ?? false,
      catalogBestSellerPosition: product.catalogBestSellerPosition ?? null,
      catalogBadgeText: product.catalogBadgeText ?? '',
      sendToKitchen: product.sendToKitchen ?? true,
      prepRequiresMode: product.prepRequiresMode ?? 'inherit',
      prepTimeMinutes: product.prepTimeMinutes ?? null,
      allowHalfAndHalf: product.allowHalfAndHalf ?? false,
      fulfillmentLeadTimeMode: product.fulfillmentLeadTimeMode ?? 'inherit',
      fulfillmentLeadTimeDays: product.fulfillmentLeadTimeDays ?? 0,
      fulfillmentReadyTime: product.fulfillmentReadyTime ?? null,
      active: product.active,
      isSupplyItem: product.isSupplyItem ?? false,
      categoryIds: assignedCategoryIdsFromProduct(product),
      sku: product.sku ?? '',
      stockQuantity: product.stockQuantity ?? null,
      // Agregado desde variantes en productos variables: una variante sin
      // vínculo (o con item inactivo) no debe apagar el toggle si otras sí
      // están gestionadas (ver product-inventory-state.utils).
      controlInventory: inventoryManaged,
      inventoryManagedAtLoad: inventoryManaged,
      inventoryItemIdAtLoad: product.inventoryItemId ?? null,
      weightGrams: product.weightGrams ?? null,
      lengthCm: product.lengthCm ?? null,
      widthCm: product.widthCm ?? null,
      heightCm: product.heightCm ?? null,
    };
  }

  private resolveImagePreviewUrl(product: Product): string | null {
    if (!product.imageUrl) {
      return null;
    }

    return mediaUrlForContext(product.imageUrls, 'form-preview', product.imageUrl);
  }
}
